/**
 * RefereeList
 * Renders a scrollable list of referees with a collapsible email selection UI per referee.
 * Each referee panel provides:
 * - A parent checkbox to select/deselect all email addresses for that referee
 * - Child checkboxes for individual email addresses via `Checkbox.Group`
 * The component maintains a parent-controlled state map of selected email IDs per referee
 * and exposes actions to open messaging/email modals with the current selections.
 *
 * State & data:
 * - Builds `emailItemsData` (refereeId -> EmailObjectId[]) from `tableData` via `useMemo`.
 * - Controls `selectedEmailItems` (refereeId -> EmailObjectId[]) via `useState`.
 * - Initializes selections to "all" per referee when data changes (`useEffect`).
 * - Uses stable handlers (`useCallback`) to update only affected referee entries,
 *   minimizing child re-renders alongside `React.memo`-wrapped items.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.tableData Array of referee objects; each must include `ObjectId`, `Name`, and `ClientList.EmailAddressList`.
 * @param {Function} props.openModal Function to open modals; used for message/email actions.
 * @returns {JSX.Element} Scrollable referee list with per-referee email selection controls.
 */

import Icon, { UserOutlined } from "@ant-design/icons";
import { Avatar, Card, Checkbox, Col, Collapse, Button as AntButton, Row, Space, Typography } from "antd";
import Button from "@ui/form-controls/button";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as PhoneCall } from "@assets/images/svg/phoneCall.svg";

import { getColor } from "../../helper";

import CallModal from "@ui/candidate/call-modal";

const CheckboxGroup = Checkbox.Group;
const { Text } = Typography;
const { Panel } = Collapse;

function RefereeList({ tableData, openModal }) {
  // Track selected emails per referee in state (controlled by parent)
  const [selectedEmailItems, setSelectedEmailItems] = useState({});

  // Build map of refereeId -> all available email addresses for that referee
  const emailItemsData = useMemo(() => {
    const map = {};
    tableData?.forEach(referee => {
      const emails =
        referee?.ClientList?.EmailAddressList?.map(contact => contact.EmailObjectId) || [];
      if (emails.length) map[referee.ObjectId] = emails;
    });
    return map;
  }, [tableData]);

  // Stable empty array reference to avoid prop changes
  const EMPTY = useMemo(() => [], []);

  // Update only the changed referee's selection
  const onChangeChildCheckbox = useCallback((refereeId, checkedValues) => {
    setSelectedEmailItems(prev => {
      if (prev[refereeId] === checkedValues) return prev;
      return { ...prev, [refereeId]: checkedValues };
    });
  }, []);

  const onChangeParentCheckbox = useCallback(
    (e, refereeId) => {
      const next = e.target.checked ? emailItemsData[refereeId] || EMPTY : EMPTY;
      setSelectedEmailItems(prev => {
        if (prev[refereeId] === next) return prev;
        return { ...prev, [refereeId]: next };
      });
    },
    [emailItemsData, EMPTY]
  );

  const selectAllIdsHandler = e => {
    if (e.target.checked) {
      setSelectedEmailItems(emailItemsData);
    } else {
      setSelectedEmailItems({});
    }
  };

  // Initialize selections when data changes (default: all checked per referee)
  useEffect(() => {
    setSelectedEmailItems(emailItemsData);
  }, [emailItemsData]);

  return (
    <>
      {/* send msg/email Buttons */}
      <Row justify="end">
        <Col>
          <Button
            variant="primary"
            onClick={() =>
              openModal({
                modalName: "SendEmailByTemplateToSiteModal",
                selectedEmailItems: selectedEmailItems
              })
            }
          >
            Send Email
          </Button>
        </Col>
      </Row>

      <Row align="middle" gutter={8}>
        <Col>
          <Checkbox
            indeterminate={
              Object.values(selectedEmailItems).flat().length !== 0 &&
              Object.values(selectedEmailItems).flat().length !==
                Object.values(emailItemsData).flat().length
            }
            checked={
              Object.values(selectedEmailItems).flat().length ===
              Object.values(emailItemsData).flat().length
            }
            onChange={e => selectAllIdsHandler(e)}
          />
        </Col>
        <Col>
          <Text className="font-bold" level={5}>
            Select All Referees Contacts ({`${Object.values(selectedEmailItems).flat().length}`})
          </Text>
        </Col>
      </Row>

      <Card className="referee-list-card">
        {tableData?.map(referee => (
          <RefereeListItem
            key={referee.ObjectId}
            referee={referee}
            refereeId={referee.ObjectId}
            checkedList={selectedEmailItems[referee.ObjectId] || EMPTY}
            onChangeChildCheckbox={onChangeChildCheckbox}
            onChangeParentCheckbox={onChangeParentCheckbox}
          />
        ))}
      </Card>
    </>
  );
}

export default RefereeList;

const RefereeListItem = React.memo(function RefereeListItem(props) {
  const { referee, refereeId, checkedList, onChangeChildCheckbox, onChangeParentCheckbox } = props;
  const plainOptions =
    referee?.ClientList?.EmailAddressList?.map(contact => contact.EmailObjectId) || [];

  const checkAll = plainOptions.length > 0 && checkedList.length === plainOptions.length;
  const indeterminate = checkedList.length > 0 && checkedList.length < plainOptions.length;

  const onChildChange = list => {
    onChangeChildCheckbox(refereeId, list);
  };

  const onParentChange = e => {
    onChangeParentCheckbox(e, refereeId);
  };

  return (
    <CollapsableReferee
      checkedList={checkedList}
      onCheckboxChange={onChildChange}
      contactDetails={referee.ClientList}
      key={referee.ObjectId}
      header={referee.Name}
    >
      <Row justify="space-between" align="middle">
        <Col span={23}>
          <Space size="small">
            <Avatar
              style={{
                backgroundColor: "green",
                verticalAlign: "middle"
              }}
              size="small"
            >
              <UserOutlined />
            </Avatar>

            <Checkbox indeterminate={indeterminate} onChange={onParentChange} checked={checkAll} />
            {referee.Name}

            {referee?.Badges &&
              Array.isArray(referee?.Badges) &&
              referee?.Badges.map((data, idx) => (
                <AntButton
                  key={idx}
                  style={{ backgroundColor: getColor(data.BadgeType) }}
                  className="cursor-default"
                >
                  {data?.Text}
                </AntButton>
              ))}

            {referee.ClientList.EmailAddressList.length === 0 && (
              <Text className="table-td-color text-xs">No contact details</Text>
            )}
          </Space>
        </Col>

        <Col>
          <CallModal personObjectId={referee.ObjectId} candidateName={referee.Name}>
            <Icon className="cursor-pointer text-xl" component={PhoneCall} />
          </CallModal>
        </Col>
      </Row>
    </CollapsableReferee>
  );
});

/**
 * CollapsableReferee - A collapsible component that displays referee contact details
 *
 * This component renders an Ant Design Collapse panel containing email address
 * and phone number contact information for a referee. Each contact entry is displayed
 * with a checkbox and formatted contact details.
 *
 * @param {Object} props - The component props
 * @param {Object} props.contactDetails - Contact information object containing email and phone lists
 * @param {Array} props.contactDetails.EmailAddressList - Array of email contact objects
 * @param {string} props.contactDetails.EmailAddressList[].EmailObjectId - Contact's email object ID
 * @param {string} props.contactDetails.EmailAddressList[].forename - Contact's first name
 * @param {string} props.contactDetails.EmailAddressList[].surname - Contact's last name
 * @param {string} props.contactDetails.EmailAddressList[].job_title - Contact's job title
 * @param {string} props.contactDetails.EmailAddressList[].contact_description - Contact's description
 * @param {Array} props.contactDetails.PhoneNumberList - Array of phone contact objects (currently commented out)
 * @param {string|number} props.key - Unique key identifier for the collapse panel
 * @param {React.ReactNode} props.children - React children elements to be rendered as the collapse header
 *
 * @returns {JSX.Element} A Collapse component with Panel containing formatted contact details
 *
 */
const CollapsableReferee = ({ contactDetails, key, children, onCheckboxChange, checkedList }) => {
  const { EmailAddressList } = contactDetails;
  const emailItemsToRender = [
    "email_address",
    "forename",
    "surname",
    "job_title",
    "contact_description"
  ];

  const renderItem = (entryItem, keysToRender) => {
    return keysToRender
      .map(key => entryItem[key])
      .filter(Boolean)
      .join(" - ");
  };

  const onChange = checkedValues => {
    onCheckboxChange(checkedValues);
  };

  return (
    <Collapse
      collapsible="icon"
      className="collapsable-referee pt-1 pb-1 bg-transparent rounded-none border-0 border-b border-gray"
    >
      <Panel header={children} key={key}>
        <CheckboxGroup value={checkedList} onChange={onChange}>
          {EmailAddressList.length === 0 ? (
            <Text className="table-td-color">No contacts available.</Text>
          ) : (
            EmailAddressList.map((item, idx) => {
              return (
                <div className="flex gap-2" key={key + "EmailEntry" + idx}>
                  <Checkbox value={item.EmailObjectId} />
                  <span className="table-td-color">{renderItem(item, emailItemsToRender)}</span>
                </div>
              );
            })
          )}
        </CheckboxGroup>
      </Panel>
    </Collapse>
  );
};
